import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useAffiliateAuth } from '../../contexts/AffiliateAuthContext';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  CheckCircle, 
  AlertCircle, 
  Mail, 
  RotateCcw,
  ArrowLeft,
  CreditCard
} from 'lucide-react';
import AuthFloatingControls from './AuthFloatingControls.jsx';

const EmailVerification = () => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check if this is an affiliate verification route
  const isAffiliateRoute = location.pathname.startsWith('/affiliate/verify-email');
  
  // Use appropriate context based on route
  const regularAuth = useAuth();
  const affiliateAuth = useAffiliateAuth();
  
  const { verifyEmail, resendVerification } = isAffiliateRoute ? affiliateAuth : regularAuth;
  
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [userId, setUserId] = useState('');
  const [requiresPayment, setRequiresPayment] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState('');

  const token = searchParams.get('token');

  const localizeVerificationText = (text) => {
    if (!text) return text;
    const map = {
      'Email has already been verified.': t('auth.emailAlreadyVerified'),
      'This verification token has already been used.': t('auth.verificationTokenAlreadyUsed')
    };
    return map[text] || text;
  };

  useEffect(() => {
    if (token) {
      handleVerification();
    }
  }, [token]);

  const handleVerification = async () => {
    if (!token) return;

    setLoading(true);
    setError('');
    setMessage('');

    try {
      const result = await verifyEmail(token);
      
      if (result.success) {
        setSuccess(true);
        setMessage(localizeVerificationText(result.message));
        setRequiresPayment(result.requiresPayment !== false && !isAffiliateRoute);
        if (result.requiresPayment !== false && !isAffiliateRoute) {
          // Route to the same paywall UI used after login for unpaid users.
          navigate('/payment-required', { replace: true });
        } else {
          setTimeout(() => {
            navigate(isAffiliateRoute ? '/affiliate/login' : '/dashboard');
          }, 3000);
        }
      } else {
        setError(localizeVerificationText(result.error));
      }
    } catch (err) {
      setError(t('auth.unexpectedError'));
    } finally {
      setLoading(false);
    }
  };

  const handlePayNow = async () => {
    setPaymentError('');
    setPaymentLoading(true);
    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
      const token = localStorage.getItem('token');
      if (!token) {
        setPaymentError(t('auth.sessionExpired') || 'Session expired. Please log in again.');
        setPaymentLoading(false);
        return;
      }
      const response = await fetch(`${API_BASE_URL}/payment/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (!response.ok) {
        setPaymentError(data.message || data.detail || 'Failed to start payment');
        setPaymentLoading(false);
        return;
      }
      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
        return;
      }
      setPaymentError('Payment link not available');
    } catch (err) {
      setPaymentError(err.message || 'Network error');
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!userId) {
      setError(t('auth.userIdRequired'));
      return;
    }

    setResendLoading(true);
    setError('');

    try {
      const result = await resendVerification(userId);
      
      if (result.success) {
        setMessage(result.message);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(t('auth.unexpectedError'));
    } finally {
      setResendLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen relative overflow-hidden ${i18n.language === 'ar' ? 'rtl' : 'ltr'}`}>
        <AuthFloatingControls />
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-300 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-yellow-300 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
          <div className="absolute top-40 left-40 w-80 h-80 bg-pink-300 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>
        </div>

        <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
          <div className="max-w-md w-full">
            <div className="glass-card">
              <div className={`text-center py-8 ${i18n.language === 'ar' ? 'font-arabic' : ''}`}>
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-white mx-auto mb-6"></div>
                <h2 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>
                  {t('auth.verifyingEmail')}
                </h2>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen relative overflow-hidden ${i18n.language === 'ar' ? 'rtl' : 'ltr'}`}>
      <AuthFloatingControls />
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-300 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-yellow-300 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
        <div className="absolute top-40 left-40 w-80 h-80 bg-pink-300 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>
      </div>

      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="glass-card">
            <div className="py-8">
              {success ? (
                <div className={`text-center ${i18n.language === 'ar' ? 'font-arabic' : ''}`}>
                  <CheckCircle className="h-20 w-20 text-green-500 mx-auto mb-6" />
                  <h1 className="text-3xl font-bold mb-4 text-green-500">
                    {t('auth.emailVerified')}
                  </h1>
                  <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>
                    {message}
                  </p>
                  {requiresPayment ? (
                    <>
                      <div className={`mb-6 p-4 rounded-xl ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`} style={{ 
                        background: 'rgba(99, 102, 241, 0.1)', 
                        border: '1px solid rgba(99, 102, 241, 0.2)',
                        color: 'var(--text-primary)'
                      }}>
                        <p className="text-sm mb-4">
                          {t('auth.completeSubscription') || 'Complete your annual subscription to use the app.'}
                        </p>
                        {paymentError && (
                          <p className="text-sm mb-3" style={{ color: 'var(--error-color)' }}>{paymentError}</p>
                        )}
                        <button 
                          className="glass-button w-full py-3 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                          onClick={handlePayNow}
                          disabled={paymentLoading}
                        >
                          {paymentLoading ? (
                            <>
                              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                              <span>{t('auth.loading') || 'Loading...'}</span>
                            </>
                          ) : (
                            <>
                              <CreditCard className="h-5 w-5" />
                              <span>{t('auth.payNow') || 'Pay now'}</span>
                            </>
                          )}
                        </button>
                      </div>
                      <button 
                        className="glass-button-outline w-full py-3 text-lg font-semibold mt-3"
                        onClick={() => navigate('/login')}
                      >
                        {t('auth.goToLogin')}
                      </button>
                    </>
                  ) : (
                    <>
                      <div className={`mb-6 p-4 rounded-xl flex items-center ${i18n.language === 'ar' ? 'space-x-reverse space-x-3' : 'space-x-3'}`} style={{ 
                        background: 'rgba(34, 197, 94, 0.1)', 
                        border: '1px solid rgba(34, 197, 94, 0.2)',
                        color: 'var(--success-color)'
                      }}>
                        <CheckCircle className="h-5 w-5 flex-shrink-0" />
                        <span className="text-sm">{t('auth.redirectingToDashboard')}</span>
                      </div>
                      <button 
                        className="glass-button w-full py-3 text-lg font-semibold"
                        onClick={() => navigate(isAffiliateRoute ? '/affiliate/login' : '/dashboard')}
                      >
                        {isAffiliateRoute ? t('auth.backToLogin') : t('auth.goToDashboard')}
                      </button>
                    </>
                  )}
                </div>
              ) : (
                <div className={`text-center ${i18n.language === 'ar' ? 'font-arabic' : ''}`}>
                  <Mail className="h-20 w-20 mx-auto mb-6" style={{ color: 'var(--accent-primary)' }} />
                  <h1 className="text-3xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>
                    {t('auth.emailVerification')}
                  </h1>
                  
                  {error && (
                    <div className={`mb-6 p-4 rounded-xl flex items-center ${i18n.language === 'ar' ? 'space-x-reverse space-x-3' : 'space-x-3'}`} style={{ 
                      background: 'rgba(239, 68, 68, 0.1)', 
                      border: '1px solid rgba(239, 68, 68, 0.2)',
                      color: 'var(--error-color)'
                    }}>
                      <AlertCircle className="h-5 w-5 flex-shrink-0" />
                      <span className="text-sm">{error}</span>
                    </div>
                  )}
                  
                  {message && (
                    <div className={`mb-6 p-4 rounded-xl flex items-center ${i18n.language === 'ar' ? 'space-x-reverse space-x-3' : 'space-x-3'}`} style={{ 
                      background: 'rgba(34, 197, 94, 0.1)', 
                      border: '1px solid rgba(34, 197, 94, 0.2)',
                      color: 'var(--success-color)'
                    }}>
                      <CheckCircle className="h-5 w-5 flex-shrink-0" />
                      <span className="text-sm">{message}</span>
                    </div>
                  )}

                  {!token && (
                    <div>
                      <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>
                        {t('auth.checkEmailForVerification')}
                      </p>
                      
                      <div className="border-t border-gray-300 my-6"></div>
                      
                      <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                        {t('auth.didntReceiveEmail')}
                      </h3>
                      
                      <div className="mb-6">
                        <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>
                          {t('auth.enterUserIdToResend')}
                        </p>
                        <input
                          type="text"
                          placeholder={t('auth.enterUserId')}
                          value={userId}
                          onChange={(e) => setUserId(e.target.value)}
                          className={`glass-input w-full ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}
                          dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
                        />
                      </div>
                      
                      <button
                        className="glass-button w-full py-3 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed mb-4"
                        onClick={handleResendVerification}
                        disabled={resendLoading || !userId}
                      >
                        {resendLoading ? (
                          <div className={`flex items-center justify-center ${i18n.language === 'ar' ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                            <span>{t('auth.sending')}</span>
                          </div>
                        ) : (
                          <div className={`flex items-center justify-center ${i18n.language === 'ar' ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
                            <RotateCcw className="h-5 w-5" />
                            <span>{t('auth.resendVerificationEmail')}</span>
                          </div>
                        )}
                      </button>
                      
                      <div className="mt-6">
                        <button 
                          className="glass-button-outline w-full py-3 text-lg font-semibold"
                          onClick={() => navigate('/login')}
                        >
                          <div className={`flex items-center justify-center ${i18n.language === 'ar' ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
                            <ArrowLeft className="h-5 w-5" />
                            <span>{t('auth.backToLogin')}</span>
                          </div>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerification;
